'use client';

import { getNotifications } from '@/app/api/notifications/actions';
import {
    NotificationDto,
    NotificationSchema,
} from '@/lib/api/schemas/notification.schema';
import { supabase } from '@/lib/api/utils/supabase-client';
import { getWorkerUrl } from '@/lib/requests/worker/common';
import { WebsocketClientSimple, WSEvent } from '@/lib/websocket';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import _ from 'underscore';

type NotificationContextType = {
    notifications: NotificationDto[];
    notificationsEnd: boolean;
    notificationsUnread: number;
    notificationsLoading: boolean;
    loadNotifications: (reload: boolean) => void;
};

export const NotificationContext = createContext<
    NotificationContextType | undefined
>(undefined);

type NotificationProviderProps = {
    children: React.ReactNode;
    initialNotifications?: NotificationDto[];
    initialNotificationsEnd?: boolean;
    initialNotificationsUnread?: number;
};

export const NotificationProvider = ({
    children,
    initialNotifications,
    initialNotificationsEnd,
    initialNotificationsUnread,
}: NotificationProviderProps) => {
    // TODO other things might (will) use websocket, move back to app context
    const [websock] = useState(
        new WebsocketClientSimple(
            getWorkerUrl('services', '/notifications', 'wss'),
        ),
    );

    const [notifications, setNotifications] = useState<NotificationDto[]>(
        initialNotifications ?? [],
    );
    const [notificationsLoading, setNotificationsLoading] = useState(false);
    const [notificationsEnd, setNotificationsEnd] = useState(
        initialNotificationsEnd ?? false,
    );
    const [notificationsUnread, setNotificationsUnread] = useState(
        initialNotificationsUnread ?? 0,
    );

    const loadNotifications = useCallback(() => {
        if (notificationsLoading || notificationsEnd) return;
        setNotificationsLoading(true);
        let fromId = undefined;
        if (notifications.length > 0)
            fromId = notifications[notifications.length - 1].id;
        getNotifications(fromId).then((newNotifications) => {
            if (newNotifications instanceof Error) {
                return; // TODO ehh how to implement this
            }
            setNotificationsUnread(newNotifications.unread);
            setNotifications((current) =>
                _.uniq([...current, ...newNotifications.data], false, 'id'),
            );
            setNotificationsLoading(false);
            if (newNotifications.end) setNotificationsEnd(true);
        });
    }, [notificationsLoading, notificationsEnd, notifications]);

    const clearNotificationState = useCallback(() => {
        setNotifications([]);
        setNotificationsEnd(false);
        setNotificationsUnread(0);
        setNotificationsLoading(false);
    }, []);

    const prependNotifications = useCallback(
        (newNotifications: NotificationDto[]) => {
            setNotifications((current) => {
                // this can be done faster but who cares with this num of entries
                const result = _.uniq(
                    [...current, ...newNotifications],
                    false,
                    'id',
                );
                return _.chain(result)
                    .sortBy('id')
                    .sortBy('bumped_at')
                    .reverse()
                    .value();
            });
            return notifications.some((n) =>
                newNotifications.some((nn) => nn.id === n.id),
            );
        },
        [notifications],
    );

    useEffect(() => {
        if (!initialNotifications) loadNotifications();
    }, [initialNotifications]);

    useEffect(() => {
        const handleEvent = (...[id, topic, payload]: WSEvent) => {
            if (topic) return;
            try {
                const notification = NotificationSchema.parse(payload);
                if (notification.type === 'notification-viewed') {
                    setNotificationsUnread((current) =>
                        notification.viewed_at ? current-- : current++,
                    );
                    setNotifications((current) => {
                        return current.map((n) =>
                            n.id === id
                                ? { ...n, viewed_at: notification.viewed_at }
                                : n,
                        );
                    });
                    return;
                }

                setNotificationsUnread((current) => current + 1);
                prependNotifications([notification]);
            } catch (e) {
                console.error('Error parsing notification', e, payload);
            }
        };

        const handleConnected = async () => {
            setNotificationsLoading(true);
            getNotifications().then((newNotifications) => {
                if (newNotifications instanceof Error) {
                    return; // TODO ehh how to implement this
                }
                setNotificationsUnread(newNotifications.unread);
                const overlap = prependNotifications(newNotifications.data);
                if (!overlap) {
                    setNotifications(newNotifications.data);
                    setNotificationsEnd(newNotifications.end);
                }
                setNotificationsLoading(false);
            });
        };

        websock.on('event', handleEvent);
        websock.on('connected', handleConnected);

        return () => {
            websock.removeListener('event', handleEvent);
            websock.removeListener('connected', handleConnected);
        };
    }, [websock]);

    useEffect(() => {
        const { data: listener } = supabase.auth.onAuthStateChange(
            (_, session) => {
                if (session?.user) {
                    websock.connect(session.access_token);
                } else {
                    clearNotificationState();
                    websock.disconnect();
                }
            },
        );

        return () => listener?.subscription.unsubscribe();
    }, [supabase, websock]);

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                notificationsEnd,
                notificationsUnread,
                notificationsLoading,
                loadNotifications,
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
};

export function useNotificationContext() {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error(
            'useNotificationContext must be used within a NotificationProvider',
        );
    }
    return context;
}
